// utils/heic.ts — fotos HEIC/HEIF (la cámara del iPhone) a JPEG, en el navegador.
//
// El servidor no acepta HEIC (sharp no trae el decodificador HEVC) y ningún
// navegador salvo Safari lo muestra, así que una foto HEIC tiene que convertirse
// ANTES de subir. Safari la decodifica solo (y iOS ya entrega JPEG cuando el
// input pide `image/*`, aunque no se probó en un iPhone real); en el resto se usa libheif compilado a WebAssembly,
// cargado bajo demanda: son ~2 MB que solo bajan quienes realmente suben un HEIC.
//
// Por qué libheif-js y no `heic2any`: heic2any crea un Worker desde un blob y
// evalúa texto como JavaScript, y la CSP de la app (index.html) no lo permite;
// con ella se quedaba colgado sin error. libheif-js (WASM en el hilo principal)
// solo necesita `'wasm-unsafe-eval'` en `script-src`, que NO habilita `eval`.
// Medido en Chromium con la CSP real: 12 MP en ~1,7 s en un PC de escritorio.

/** ¿Es una foto HEIC/HEIF? Por tipo o, si el navegador no lo informó, por extensión. */
export const esHeic = (file: { type?: string; name?: string }): boolean =>
  /^image\/hei[cf](-sequence)?$/i.test(file.type ?? "") || /\.hei[cf]$/i.test(file.name ?? "");

/** Tope de píxeles a decodificar: el RGBA completo cuesta 4 bytes por píxel en memoria. */
export const MAX_MEGAPIXELES_HEIC = 64;

const MENSAJE_NO_SE_PUDO =
  "No se pudo leer la foto (formato HEIC). Vuelve a tomarla o guárdala como JPG.";

/** Error con mensaje listo para mostrar al usuario. */
export class HeicError extends Error {
  constructor(message: string = MENSAJE_NO_SE_PUDO) {
    super(message);
    this.name = "HeicError";
  }
}

// Lo mínimo de libheif-js que se usa (el paquete trae sus tipos solo para la ruta sin bundle).
interface ImagenHeif {
  get_width(): number;
  get_height(): number;
  display(destino: ImageData, cb: (resultado: ImageData | null) => void): void;
}
interface Libheif {
  HeifDecoder: new () => { decode(datos: Uint8Array): ImagenHeif[] };
}

let _libheif: Promise<Libheif> | null = null;

/** Carga el decodificador una sola vez; si falla (sin red) se puede reintentar. */
const cargarLibheif = (): Promise<Libheif> => {
  if (!_libheif) {
    _libheif = import("libheif-js/libheif-wasm/libheif-bundle.mjs")
      .then((m) => m.default() as Promise<Libheif>)
      .catch((e) => {
        _libheif = null;
        throw e;
      });
  }
  return _libheif;
};

const aBlob = (canvas: HTMLCanvasElement, calidad: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new HeicError())), "image/jpeg", calidad);
  });

/**
 * Convierte una foto HEIC/HEIF en un JPEG (mismo nombre, extensión `.jpg`).
 * Lanza `HeicError` —con mensaje para el usuario— si no se puede decodificar,
 * es demasiado grande o la librería no carga.
 */
export async function heicAJpeg(file: File, calidad = 0.92): Promise<File> {
  let canvas: HTMLCanvasElement | null = null;
  try {
    const libheif = await cargarLibheif();
    const datos = new Uint8Array(await file.arrayBuffer());
    const [imagen] = new libheif.HeifDecoder().decode(datos);
    if (!imagen) throw new HeicError();

    const w = imagen.get_width();
    const h = imagen.get_height();
    if (!(w > 0 && h > 0) || (w * h) / 1e6 > MAX_MEGAPIXELES_HEIC) {
      throw new HeicError(
        `La foto es demasiado grande para convertirla (${Math.round((w * h) / 1e6)} MP). Tómala con menor resolución.`,
      );
    }

    canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new HeicError();
    const pixeles = ctx.createImageData(w, h);
    const listo = await new Promise<ImageData | null>((resolve) => imagen.display(pixeles, resolve));
    if (!listo) throw new HeicError();
    ctx.putImageData(listo, 0, 0);

    const blob = await aBlob(canvas, calidad);
    const nombre = file.name.replace(/\.[^.]+$/, "") || "foto";
    return new File([blob], `${nombre}.jpg`, { type: "image/jpeg", lastModified: file.lastModified });
  } catch (e) {
    throw e instanceof HeicError ? e : new HeicError();
  } finally {
    if (canvas) {
      // Safari iOS tiene un tope global de memoria de canvas: se suelta de inmediato.
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
